import * as React from 'react';
import { STATUS_OK, COMMENT_SENDING_FAILED } from 'constants/review';
import { localize } from 'core/localization';
import eventEmitter, { events } from 'core/events/eventEmitter';
import LoginForm from '../LoginForm/LoginForm';
import CommentForm from '../CommentForm/CommentForm';
import {
  FormWrapper,
  FormContent,
  FormActions,
  SubmitIcon,
  FormSubmitButton,
  FormCancelButton,
  SuccessMessage,
  SuccessMessageIcon,
  ErrorMessage,
  ErrorMessageIcon
} from './ReviewForm.styled';

type ReviewFormProps = {
  context: { [key: string]: any };
  closePopover(e?: any): void;
  isGeneral: boolean;
  role: string;
  ariaModal: boolean;
  tabIndex: number;
  theme?: { [key: string]: any }
};

type ReviewFormState = {
  requestSending: boolean;
  commentText: string;
  isValidComment: boolean;
  showSuccessMessage: boolean;
  showErrorMessage: boolean;
  showLoginForm: boolean;
  name: string;
  email: string;
  validationStatusBeforeSubmit: {
    name: boolean;
    email: boolean;
  }
}
export class ReviewForm extends React.Component<ReviewFormProps, ReviewFormState> {
  loginRefs: { [key: string]: any };

  constructor(props: ReviewFormProps) {
    super(props);

    this.state = {
      requestSending: false,
      commentText: '',
      isValidComment: true,
      showSuccessMessage: false,
      showErrorMessage: false,
      showLoginForm: false,
      name: '',
      email: '',
      validationStatusBeforeSubmit: {
        name: true,
        email: true
      }
    };
    this.getReviewerEmailAndName();
    this.loginRefs = {
      name: '',
      email: ''
    };
  }

  getReviewerEmailAndName = async () => {
    const response: any = await eventEmitter.emit(events.REVIEW_RESTORE_USER);
    this.setState({
      name: response[0].name || '',
      email: response[0].email || ''
    });
  };

  beforeSubmitValidation = () => {
    const nameValid = this.loginRefs.name ? this.loginRefs.name.validity.valid : false;
    const emailValid = this.loginRefs.email ? this.loginRefs.email.validity.valid : false;
    this.setState({
      validationStatusBeforeSubmit: {
        name: nameValid,
        email: emailValid
      }
    });
  };

  checkInputValidation = (event: any) => {
    if (event) {
      this.setState({
        validationStatusBeforeSubmit: {
          ...this.state.validationStatusBeforeSubmit,
          [event.target.name]: event.target.validity.valid || false
        }
      });
    }
  };

  onChange = (event: any) => {
    this.setState({ [event.target.name]: event.target.value } as any);
  };

  onBlur = (event: any) => {
    this.setState({ [event.target.name]: event.target.value } as any);
    return this.state.showLoginForm && this.checkInputValidation(event);
  };

  getRefs = (element: any) => {
    if (element) {
      this.loginRefs[element.name] = element;
    }
  };

  validateComment = () => {
    const {
      email,
      name,
      commentText,
      showLoginForm,
      validationStatusBeforeSubmit,
      requestSending
    } = this.state;

    const isValidBeforeSubmit =
      validationStatusBeforeSubmit.email && validationStatusBeforeSubmit.name;

    if (!commentText.trim()) {
      this.setState({ isValidComment: false });
      return false;
    }
    if (!email || !name) {
      this.setState({
        showLoginForm: true
      });
      return false;
    }
    if ((showLoginForm && !isValidBeforeSubmit) || requestSending) {
      return false;
    }

    return true;
  };

  sendComment = async (event: any) => {
    event.preventDefault();
    if (this.state.showLoginForm) {
      this.checkInputValidation(event);
      this.beforeSubmitValidation();
    }
    const { context, isGeneral } = this.props;
    const { email, name, commentText } = this.state;
    if (!this.validateComment()) {
      return;
    }
    eventEmitter.emit(events.REVIEWER_AUTHORIZED, { email, name });
    const data: { [key: string]: any } = {
      text: commentText
    };

    if (!isGeneral) {
      data.context = context;
    }
    this.setState({
      showSuccessMessage: false,
      showErrorMessage: false,
      requestSending: true
    });
    const response: any = await eventEmitter.emit(events.COMMENT_SENT, { data });

    if (response[0] === STATUS_OK) {
      this.setState({
        showSuccessMessage: true,
        showErrorMessage: false,
        showLoginForm: false,
        isValidComment: true,
        commentText: '',
        requestSending: false
      });
    }

    if (response[0] === COMMENT_SENDING_FAILED) {
      this.setState({
        showErrorMessage: true,
        showSuccessMessage: false,
        requestSending: false
      });
    }
  };

  componentDidUpdate() {
    const activeElem = document.activeElement;
    if (
      activeElem && activeElem.getAttribute('name') !== 'commentText' &&
      this.loginRefs.commentText
    ) {
      setTimeout(() => {
        this.loginRefs.commentText.focus();
      }, 0);
    }
  }

  render() {
    const { closePopover, context, isGeneral } = this.props;
    const {
      showSuccessMessage,
      showErrorMessage,
      showLoginForm,
      requestSending,
      commentText,
      isValidComment
    } = this.state;
    const { name, email } = this.state.validationStatusBeforeSubmit;
    return (
      <FormWrapper>
        <FormContent theme={this.props.theme}>
          {showLoginForm ? (
            <LoginForm
              getRefs={this.getRefs}
              name={name}
              email={email}
              onChange={this.onChange}
              onBlur={this.onBlur}
              theme={this.props.theme}
            />
          ) : (
              <CommentForm
                getRefs={this.getRefs}
                commentText={commentText}
                isValidComment={isValidComment}
                onChange={this.onChange}
                context={context}
                isGeneral={isGeneral}
              />
            )}
          <FormActions role="presentation">
            <FormSubmitButton
              tabIndex={1}
              disabled={requestSending ? 'disabled' : false}
              onClick={this.sendComment}
              onKeyDown={(event: any) => {
                if (event.key === 'Enter') {
                  this.sendComment(event);
                }
              }}
            >
              <SubmitIcon size={9} name={'arrow-send'} theme={this.props.theme} />
              {showLoginForm ? localize('[post comment]') : localize('[post]')}
            </FormSubmitButton>
            <FormCancelButton
              tabIndex={1}
              onClick={closePopover}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  closePopover(event);
                }
              }}
            >
              {localize('[cancel]')}
            </FormCancelButton>
          </FormActions>
          {showSuccessMessage && (
            <SuccessMessage>
              <SuccessMessageIcon theme={this.props.theme} size={9} name={'check-mark-2'} />
              {localize('[comment has been sent successfully]')}
            </SuccessMessage>
          )}
          {showErrorMessage && (
            <ErrorMessage>
              <ErrorMessageIcon>{'!'}</ErrorMessageIcon>
              {localize('[error comment sending]')}
            </ErrorMessage>
          )}
        </FormContent>
      </FormWrapper>
    );
  }
}

export default ReviewForm;
